import { ALL_COUNTRY, countiesText, cuiProblem, RO_COUNTIES, type PublicOffer, t } from '@procumi/shared';
import { useState } from 'react';
import { api, errMsg } from '../lib/api';

/**
 * Datele firmei, după ofertă (shared/supplierProfile.ts): opțional, ca oferta
 * să nu depindă de ele. Furnizorul trece CUI-ul, județele și telefonul; numele
 * și sediul vin de la ANAF, iar totul intră în agenda cumpărătorului.
 */
export function SupplierProfileCard({
  token,
  data,
  onSaved,
}: {
  token: string;
  data: PublicOffer;
  onSaved: (d: PublicOffer) => void;
}) {
  const p = data.profile;
  const [open, setOpen] = useState(!p.filledAt);
  const [cui, setCui] = useState(p.cui);
  const [counties, setCounties] = useState<string[]>(p.counties);
  const [phone, setPhone] = useState(p.phone);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const all = counties.includes(ALL_COUNTRY);

  const toggle = (c: string) =>
    setCounties((list) =>
      c === ALL_COUNTRY
        ? list.includes(ALL_COUNTRY)
          ? []
          : [ALL_COUNTRY]
        : list.includes(c)
          ? list.filter((x) => x !== c)
          : [...list.filter((x) => x !== ALL_COUNTRY), c],
    );

  const save = async () => {
    setErr('');
    if (cuiProblem(cui)) return setErr(t('CUI-ul nu pare corect. Verificați-l pe o factură.'));
    if (!counties.length) return setErr(t('Alegeți județele în care livrați, sau „Toată țara”.'));
    setBusy(true);
    try {
      onSaved(await api.saveSupplierProfile(token, { cui, counties, phone }));
      setOpen(false);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open && p.filledAt) {
    return (
      <section className="sheet pad so-firm" aria-labelledby="so-firm-h">
        <h2 id="so-firm-h">{t('Datele firmei')}</h2>
        <p className="so-firm-ok">
          <b>{t('{0}CUI {1}', p.name ? `${p.name} · ` : '', p.cui)}</b>
          {p.address ? <span>{p.address}</span> : null}
          <span>{t('Livrați în: {0}', countiesText(p.counties))}</span>
        </p>
        <button className="btn small" onClick={() => setOpen(true)}>
          {t('Schimbă datele')}
        </button>
      </section>
    );
  }

  return (
    <section className="sheet pad so-firm" aria-labelledby="so-firm-h">
      <h2 id="so-firm-h">{t('Datele firmei (opțional)')}</h2>
      <p className="so-lede">
        {t(
          'Ca să primiți comenzile de la {0} pe datele corecte. Numele și sediul le luăm de la ANAF după CUI.',
          data.company,
        )}
      </p>
      <label className="f">
        {t('CUI')}
        <input
          className="inp"
          value={cui}
          maxLength={20}
          placeholder="RO12345678"
          inputMode="text"
          onChange={(e) => setCui(e.target.value)}
        />
      </label>
      <div className="f">
        {t('Unde livrați')}
        <div className="so-seg so-counties">
          {[ALL_COUNTRY, ...RO_COUNTIES].map((c) => (
            <label key={c} className={counties.includes(c) ? 'on' : undefined}>
              <input
                type="checkbox"
                checked={counties.includes(c)}
                disabled={all && c !== ALL_COUNTRY}
                onChange={() => toggle(c)}
              />
              {c}
            </label>
          ))}
        </div>
      </div>
      <label className="f">
        {t('Telefon (opțional)')}
        <input
          className="inp"
          value={phone}
          maxLength={40}
          inputMode="tel"
          placeholder={t('07xx xxx xxx')}
          onChange={(e) => setPhone(e.target.value)}
        />
      </label>
      {err ? (
        <p className="so-err" role="alert">
          {err}
        </p>
      ) : null}
      <div className="so-done-acts">
        <button className="btn primary" disabled={busy} onClick={() => void save()}>
          {busy ? t('Se salvează…') : t('Salvează datele firmei')}
        </button>
        {p.filledAt ? (
          <button className="btn" disabled={busy} onClick={() => setOpen(false)}>
            {t('Renunță')}
          </button>
        ) : null}
      </div>
    </section>
  );
}
